import type { CountryCode } from 'types/countries';

export interface CountryMetadata {
    code: CountryCode;
    /** Program-list label. Not a translated UI string. */
    name: string;
    labelKey: 'australia' | 'canada' | 'unitedKingdom';
}

/** Fixed AU → CA → UK order. Availability never comes from the hostname. */
export const COUNTRIES: readonly CountryMetadata[] = [
    { code: 'AU', name: 'Australia', labelKey: 'australia' },
    { code: 'CA', name: 'Canada', labelKey: 'canada' },
    { code: 'UK', name: 'United Kingdom', labelKey: 'unitedKingdom' },
];

const COUNTRY_CODES: ReadonlySet<string> = new Set(COUNTRIES.map((country) => country.code));

function isCountryCode(value: string): value is CountryCode {
    return COUNTRY_CODES.has(value);
}

export function countryLabelKey(code: CountryCode): CountryMetadata['labelKey'] {
    const country = COUNTRIES.find((item) => item.code === code);
    if (!country) {
        throw new Error(`Unknown country code: ${code}`);
    }
    return country.labelKey;
}

/** Missing / null / omitted / non-array values are no entitlements. */
export function normalizeEnabledCountryCodes(value: unknown): CountryCode[] {
    if (!Array.isArray(value)) {
        return [];
    }

    const seen = new Set<CountryCode>();
    for (const item of value) {
        if (typeof item !== 'string') {
            continue;
        }
        const code = item.trim();
        if (!isCountryCode(code) || seen.has(code)) {
            continue;
        }
        seen.add(code);
    }

    return COUNTRIES.filter((country) => seen.has(country.code)).map((country) => country.code);
}

export function hasCountry(
    enabled: readonly string[] | null | undefined,
    code: CountryCode | string | null | undefined
): boolean {
    if (!code || !Array.isArray(enabled)) {
        return false;
    }
    return enabled.includes(code);
}

export function hasCanada(enabled: readonly string[] | null | undefined): boolean {
    return hasCountry(enabled, 'CA');
}

/** Australia or the United Kingdom. Canada alone does not qualify. */
export function hasSelfServeMarket(enabled: readonly string[] | null | undefined): boolean {
    return hasCountry(enabled, 'AU') || hasCountry(enabled, 'UK');
}

export function enabledCountryNames(enabled: readonly string[] | null | undefined): string[] {
    const codes = new Set(normalizeEnabledCountryCodes(enabled));
    return COUNTRIES.filter((country) => codes.has(country.code)).map((country) => country.name);
}

/**
 * Route roles for markets that differ.
 * Mixed providers get the union. No enabled market yields no roles.
 */
export function rolesForEnabledCountries<T extends string>(
    enabled: readonly string[] | null | undefined,
    canadaRoles: readonly T[],
    selfServeRoles: readonly T[]
): T[] {
    const includeCanada = hasCanada(enabled);
    const includeSelfServe = hasSelfServeMarket(enabled);
    if (!includeCanada && !includeSelfServe) {
        return [];
    }

    const roles: T[] = [];
    const seen = new Set<T>();
    const add = (list: readonly T[]) => {
        for (const role of list) {
            if (seen.has(role)) {
                continue;
            }
            seen.add(role);
            roles.push(role);
        }
    };

    if (includeCanada) {
        add(canadaRoles);
    }
    if (includeSelfServe) {
        add(selfServeRoles);
    }
    return roles;
}

export const SELF_SERVE_PATHWAY = '/visa-options/visa-pathway';
export const SELF_SERVE_IMPROVED_PROFILE = '/visa-options/improved-profile-pathway';
/** Authenticated page that does not depend on an enabled market. */
export const ACCOUNT_HOME = '/account/my-account';

/**
 * Self-serve assessment home. Null when Australia and the UK are both disabled,
 * including logged-out users, so callers do not send Canada-only providers there.
 */
export function selfServeEntryPath(
    enabled: readonly string[] | null | undefined,
    kind: 'qualified' | 'unqualified' = 'qualified'
): string | null {
    if (!hasSelfServeMarket(enabled)) {
        return null;
    }
    return kind === 'unqualified' ? SELF_SERVE_IMPROVED_PROFILE : SELF_SERVE_PATHWAY;
}

export function soleEnabledCountryCode(
    enabled: readonly string[] | null | undefined
): CountryCode | null {
    const codes = normalizeEnabledCountryCodes(enabled);
    return codes.length === 1 ? codes[0] : null;
}

function trimmedCountry(value: string | null | undefined): string {
    return typeof value === 'string' ? value.trim() : '';
}

/** Enabled country names, keeping a saved application country that is no longer enabled. */
export function countryNameOptions(
    enabled: readonly string[] | null | undefined,
    applicationCountry?: string | null
): string[] {
    const names = enabledCountryNames(enabled);
    const current = trimmedCountry(applicationCountry);
    if (current && !names.includes(current)) {
        return [current, ...names];
    }
    return names;
}

/**
 * A saved application country stays selected.
 * With no application country, select the only enabled country.
 * Several enabled countries stay unselected.
 */
export function chosenCountryName(
    enabled: readonly string[] | null | undefined,
    applicationCountry?: string | null
): string | null {
    const current = trimmedCountry(applicationCountry);
    if (current) {
        return current;
    }
    const names = enabledCountryNames(enabled);
    return names.length === 1 ? names[0] : null;
}

/**
 * CV extraction country. One enabled market is sent as-is.
 * Several markets use only an explicit selection. Never the first list entry.
 */
export function cvUploadCountryCode(
    enabled: readonly string[] | null | undefined,
    selected: string | null | undefined
): CountryCode | null {
    const codes = normalizeEnabledCountryCodes(enabled);
    if (codes.length === 1) {
        return codes[0];
    }
    return codes.find((code) => code === selected) ?? null;
}

/** Row country is one of the immigration destinations (English labels). */
export const DESTINATION_COUNTRIES = ['Australia', 'Canada', 'United Kingdom'] as const;

/** Education region visibility, including French labels used by Assessment forms. */
export const EDUCATION_REGION_COUNTRIES = [
    'Australia',
    'Australie',
    'Canada',
    'United Kingdom',
    'Royaume Uni',
] as const;

export const isDestinationCountry = (country?: string | null): boolean =>
    Boolean(country && (DESTINATION_COUNTRIES as readonly string[]).includes(country));

export const isEducationRegionCountry = (country?: string | null): boolean =>
    Boolean(country && (EDUCATION_REGION_COUNTRIES as readonly string[]).includes(country));

const ROW_COUNTRY_NAMES: Record<CountryCode, readonly string[]> = {
    AU: ['Australia', 'Australie'],
    CA: ['Canada'],
    UK: ['United Kingdom', 'Royaume Uni'],
};

export function rowMatchesCountry(
    rowCountry: string | null | undefined,
    code: CountryCode
): boolean {
    const name = trimmedCountry(rowCountry);
    return Boolean(name) && ROW_COUNTRY_NAMES[code].includes(name);
}

/** Institution, postcode, and nomination controls need the row country and that market. */
export function rowCountryEnabled(
    enabled: readonly string[] | null | undefined,
    rowCountry: string | null | undefined,
    code: CountryCode
): boolean {
    return hasCountry(enabled, code) && rowMatchesCountry(rowCountry, code);
}

/** Canada NOI or an Australia/UK nomination list. Hidden markets are not required. */
export function jobOfferNominationVisible(
    enabled: readonly string[] | null | undefined,
    rowCountry: string | null | undefined
): boolean {
    return (
        rowCountryEnabled(enabled, rowCountry, 'CA') ||
        rowCountryEnabled(enabled, rowCountry, 'AU') ||
        rowCountryEnabled(enabled, rowCountry, 'UK')
    );
}

function hasStoredValue(value: unknown): boolean {
    if (typeof value === 'number') {
        return !Number.isNaN(value);
    }
    if (typeof value === 'string') {
        return value.trim() !== '';
    }
    return false;
}

/**
 * Read-only profile rows keep a saved value after its market is turned off.
 * An empty row still shows when the stored country is one of `markets`.
 */
export function showHistoricalField(
    rowCountry: string | null | undefined,
    markets: readonly CountryCode[],
    storedValue: unknown
): boolean {
    if (hasStoredValue(storedValue)) {
        return true;
    }
    return markets.some((code) => rowMatchesCountry(rowCountry, code));
}

/**
 * Report download country. A selected or sole on-screen result wins.
 * A sole enabled market is the fallback. Several choices stay unresolved.
 */
export function assessmentReportCountry(
    enabled: readonly string[] | null | undefined,
    selectedCountry?: string | null,
    onScreenCountries?: readonly (string | null | undefined)[]
): string | null {
    const selected = trimmedCountry(selectedCountry);
    if (selected) {
        return selected;
    }

    const onScreen: string[] = [];
    for (const country of onScreenCountries ?? []) {
        const name = trimmedCountry(country);
        if (name && !onScreen.includes(name)) {
            onScreen.push(name);
        }
    }
    if (onScreen.length === 1) {
        return onScreen[0];
    }

    const names = enabledCountryNames(enabled);
    return names.length === 1 ? names[0] : null;
}

/** Enabled market whose name matches the row, or null when the row is some other country. */
export function enabledCountryCodeForRow(
    enabled: readonly string[] | null | undefined,
    rowCountry: string | null | undefined
): CountryCode | null {
    return (
        normalizeEnabledCountryCodes(enabled).find((code) => rowMatchesCountry(rowCountry, code)) ??
        null
    );
}

export function showInstitutionForRow(
    enabled: readonly string[] | null | undefined,
    rowCountry: string | null | undefined
): boolean {
    return enabledCountryCodeForRow(enabled, rowCountry) !== null;
}

export type FieldOfStudyIdKey =
    | 'field_of_study_au_id'
    | 'field_of_study_ca_id'
    | 'field_of_study_uk_id';

export type FieldOfStudyNameKey = 'field_of_study_au' | 'field_of_study_ca' | 'field_of_study_uk';

export type OccupationIdKey = 'occupation_au_id' | 'occupation_ca_id' | 'occupation_uk_id';

export type OccupationNameKey = 'occupation_au' | 'occupation_ca' | 'occupation_uk';

export interface MarketFields {
    code: CountryCode;
    name: string;
    occupationIdKey: OccupationIdKey;
    occupationNameKey: OccupationNameKey;
    occupationNameEnKey: `${OccupationNameKey}_en`;
    occupationLabelKey: 'occupationAU' | 'occupationCA' | 'occupationUK';
    fieldOfStudyIdKey: FieldOfStudyIdKey;
    fieldOfStudyNameKey: FieldOfStudyNameKey;
    fieldOfStudyNameEnKey: `${FieldOfStudyNameKey}_en`;
    fieldOfStudyLabelKey:
        | 'fieldOfStudyAustralia'
        | 'fieldOfStudyCanada'
        | 'fieldOfStudyUnitedKingdom';
}

const MARKET_FIELDS: Record<CountryCode, MarketFields> = {
    AU: {
        code: 'AU',
        name: 'Australia',
        occupationIdKey: 'occupation_au_id',
        occupationNameKey: 'occupation_au',
        occupationNameEnKey: 'occupation_au_en',
        occupationLabelKey: 'occupationAU',
        fieldOfStudyIdKey: 'field_of_study_au_id',
        fieldOfStudyNameKey: 'field_of_study_au',
        fieldOfStudyNameEnKey: 'field_of_study_au_en',
        fieldOfStudyLabelKey: 'fieldOfStudyAustralia',
    },
    CA: {
        code: 'CA',
        name: 'Canada',
        occupationIdKey: 'occupation_ca_id',
        occupationNameKey: 'occupation_ca',
        occupationNameEnKey: 'occupation_ca_en',
        occupationLabelKey: 'occupationCA',
        fieldOfStudyIdKey: 'field_of_study_ca_id',
        fieldOfStudyNameKey: 'field_of_study_ca',
        fieldOfStudyNameEnKey: 'field_of_study_ca_en',
        fieldOfStudyLabelKey: 'fieldOfStudyCanada',
    },
    UK: {
        code: 'UK',
        name: 'United Kingdom',
        occupationIdKey: 'occupation_uk_id',
        occupationNameKey: 'occupation_uk',
        occupationNameEnKey: 'occupation_uk_en',
        occupationLabelKey: 'occupationUK',
        fieldOfStudyIdKey: 'field_of_study_uk_id',
        fieldOfStudyNameKey: 'field_of_study_uk',
        fieldOfStudyNameEnKey: 'field_of_study_uk_en',
        fieldOfStudyLabelKey: 'fieldOfStudyUnitedKingdom',
    },
};

export function marketFields(code: CountryCode): MarketFields {
    return MARKET_FIELDS[code];
}

/** One catalog per enabled market, in AU → CA → UK order. */
export function enabledMarketFields(enabled: readonly string[] | null | undefined): MarketFields[] {
    return normalizeEnabledCountryCodes(enabled).map((code) => MARKET_FIELDS[code]);
}

export function fieldOfStudyMarketVisible(
    enabled: readonly string[] | null | undefined,
    educationType: string | null | undefined,
    code: CountryCode
): boolean {
    return Boolean(educationType) && educationType !== 'High school' && hasCountry(enabled, code);
}

export function visibleFieldOfStudyMarkets(
    enabled: readonly string[] | null | undefined,
    educationType?: string | null
): { showAu: boolean; showCa: boolean; showUk: boolean } {
    return {
        showAu: fieldOfStudyMarketVisible(enabled, educationType, 'AU'),
        showCa: fieldOfStudyMarketVisible(enabled, educationType, 'CA'),
        showUk: fieldOfStudyMarketVisible(enabled, educationType, 'UK'),
    };
}

const FIELD_OF_STUDY_MESSAGE_KEYS: readonly FieldOfStudyIdKey[] = [
    'field_of_study_au_id',
    'field_of_study_ca_id',
    'field_of_study_uk_id',
];

/** One required-message per visible market. Hidden markets stay blank. */
export function fieldOfStudyValidationMessages(
    enabled: readonly string[] | null | undefined,
    entry: {
        type?: string | null;
        field_of_study_au_id?: unknown;
        field_of_study_ca_id?: unknown;
        field_of_study_uk_id?: unknown;
    },
    requiredMessage: string
): Record<FieldOfStudyIdKey, string> {
    const messages: Record<FieldOfStudyIdKey, string> = {
        field_of_study_au_id: '',
        field_of_study_ca_id: '',
        field_of_study_uk_id: '',
    };

    for (const key of FIELD_OF_STUDY_MESSAGE_KEYS) {
        const code: CountryCode =
            key === 'field_of_study_au_id' ? 'AU' : key === 'field_of_study_ca_id' ? 'CA' : 'UK';
        if (!fieldOfStudyMarketVisible(enabled, entry.type, code)) {
            continue;
        }
        const value = entry[key];
        if (value === '' || value === undefined || value === null) {
            messages[key] = requiredMessage;
        }
    }

    return messages;
}

/** AU hour definition only when Australia is the sole enabled market. */
export function percentTimeTooltipKey(
    enabled: readonly string[] | null | undefined
): 'percentTimeTooltipAustralia' | 'percentTimeTooltip' {
    const codes = normalizeEnabledCountryCodes(enabled);
    return codes.length === 1 && codes[0] === 'AU'
        ? 'percentTimeTooltipAustralia'
        : 'percentTimeTooltip';
}

export type RelativesQuestionLabelKey =
    | 'relativesInCanada'
    | 'relativesInAustralia'
    | 'relativesInUnitedKingdom'
    | 'relativesInCanadaAustralia'
    | 'relativesInCanadaUnitedKingdom'
    | 'relativesInAustraliaUnitedKingdom'
    | 'relativesInCanadaAustraliaUnitedKingdom'
    | 'relativesQuestion';

/** Yes/no relatives prompt for one enabled market or the union of several. */
export function relativesQuestionLabelKey(
    enabled: readonly string[] | null | undefined
): RelativesQuestionLabelKey {
    const codes = new Set(normalizeEnabledCountryCodes(enabled));
    if (codes.size === 1 && codes.has('CA')) return 'relativesInCanada';
    if (codes.size === 1 && codes.has('AU')) return 'relativesInAustralia';
    if (codes.size === 1 && codes.has('UK')) return 'relativesInUnitedKingdom';
    if (codes.size === 2 && codes.has('CA') && codes.has('AU')) return 'relativesInCanadaAustralia';
    if (codes.size === 2 && codes.has('CA') && codes.has('UK')) {
        return 'relativesInCanadaUnitedKingdom';
    }
    if (codes.size === 2 && codes.has('AU') && codes.has('UK')) {
        return 'relativesInAustraliaUnitedKingdom';
    }
    if (codes.size === 3) return 'relativesInCanadaAustraliaUnitedKingdom';
    return 'relativesQuestion';
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

const readEnabledCountryCodesField = (payload: unknown): { present: boolean; value: unknown } => {
    if (!isRecord(payload)) {
        return { present: false, value: undefined };
    }

    if ('enabled_country_codes' in payload) {
        return { present: true, value: payload.enabled_country_codes };
    }

    if (isRecord(payload.data) && 'enabled_country_codes' in payload.data) {
        return { present: true, value: payload.data.enabled_country_codes };
    }

    return { present: false, value: undefined };
};

/** True when the payload includes `enabled_country_codes` (even if null / []). */
export function payloadHasEnabledCountryCodes(payload: unknown): boolean {
    return readEnabledCountryCodesField(payload).present;
}

/** Reads `enabled_country_codes` from a current-user / auth payload. Omitted → []. */
export function pickEnabledCountryCodes(payload: unknown): CountryCode[] {
    const { present, value } = readEnabledCountryCodesField(payload);
    if (!present) {
        return [];
    }
    return normalizeEnabledCountryCodes(value);
}
